import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
  MoreVert as MoreIcon,
  Input as InputIcon,
  Description as WorkInstructionIcon,
  Build as ToolFixtureIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  setOperationFilters,
  setOperationPagination,
  setOperationBulkSelection,
  setOperationDialogOpen,
  setDetailDialogOpen,
  setSelectedOperation,
} from '../../store/reducers/operationsSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  searchFilter: {
    marginBottom: theme.spacing(2),
  },
  statisticsCard: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    minWidth: 80,
  },
  bulkActions: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    margin: theme.spacing(0.25),
    minWidth: 'auto',
    width: 32,
    height: 32,
  },
  operationNumber: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

const OperationsList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    operations: {
      items: operations,
      loading,
      pagination,
      filters,
      statistics,
      bulkSelection,
    },
    dropdowns: { operationTypes, machines, parts },
  } = useSelector((state: RootState) => state.operations);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOperationId, setSelectedOperationId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    dispatch({ type: 'operations/fetchOperations' });
    dispatch({ type: 'operations/fetchDropdowns' });
  }, [dispatch]);

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setOperationFilters({ search: event.target.value }));
  };

  // Handle filters
  const handleFilterChange = (field: string, value: any) => {
    dispatch(setOperationFilters({ [field]: value === '' ? undefined : value }));
  };

  // Handle pagination
  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setOperationPagination({ page: newPage + 1, limit: pagination.limit }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setOperationPagination({ page: 1, limit: parseInt(event.target.value, 10) }));
  };

  // Handle bulk selection
  const handleBulkSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(setOperationBulkSelection(operations.map(operation => operation.Id!)));
    } else {
      dispatch(setOperationBulkSelection([]));
    }
  };

  const handleBulkSelectOne = (operationId: string) => {
    const isSelected = bulkSelection.includes(operationId);
    if (isSelected) {
      dispatch(setOperationBulkSelection(bulkSelection.filter(id => id !== operationId)));
    } else {
      dispatch(setOperationBulkSelection([...bulkSelection, operationId]));
    }
  };

  // Handle actions
  const handleCreateOperation = () => {
    dispatch(setSelectedOperation(null));
    dispatch(setOperationDialogOpen(true));
  };

  const handleEditOperation = (operation: any) => {
    dispatch(setSelectedOperation(operation));
    dispatch(setOperationDialogOpen(true));
    setAnchorEl(null);
  };

  const handleDeleteOperation = (operationId: string) => {
    setSelectedOperationId(operationId);
    setConfirmDeleteOpen(true);
    setAnchorEl(null);
  };

  const confirmDelete = () => {
    if (selectedOperationId) {
      dispatch({ type: 'operations/deleteOperation', payload: selectedOperationId });
    }
    setConfirmDeleteOpen(false);
    setSelectedOperationId(null);
  };

  const handleExport = () => {
    dispatch({ type: 'operations/exportOperations' });
  };

  const handleBulkActivate = () => {
    dispatch({ type: 'operations/bulkActivateOperations', payload: bulkSelection });
    dispatch(setOperationBulkSelection([]));
  };

  const handleBulkDeactivate = () => {
    dispatch({ type: 'operations/bulkDeactivateOperations', payload: bulkSelection });
    dispatch(setOperationBulkSelection([]));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, operationId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedOperationId(operationId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOperationId(null);
  };

  // Handle detail views
  const handleViewInputs = (operation: any) => {
    dispatch(setDetailDialogOpen({
      open: true,
      type: 'inputs',
      data: operation.Inputs || [],
      title: `Inputs: ${operation.Name}`,
    }));
    setAnchorEl(null);
  };

  const handleViewWorkInstructions = (operation: any) => {
    dispatch(setDetailDialogOpen({
      open: true,
      type: 'workInstructions',
      data: operation.WorkInstructions || [],
      title: `Work Instructions: ${operation.Name}`,
    }));
    setAnchorEl(null);
  };

  const handleViewToolFixtures = (operation: any) => {
    dispatch(setDetailDialogOpen({
      open: true,
      type: 'toolFixtures',
      data: operation.ToolFixtures || [],
      title: `Tools & Fixtures: ${operation.Name}`,
    }));
    setAnchorEl(null);
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1">
            Operations Management
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              style={{ marginRight: 8 }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateOperation}
            >
              Add Operation
            </Button>
          </Box>
        </Box>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} className={classes.statisticsCard}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Operations
                </Typography>
                <Typography variant="h5">
                  {statistics.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Operations
                </Typography>
                <Typography variant="h5" style={{ color: '#4caf50' }}>
                  {statistics.active}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Inactive Operations
                </Typography>
                <Typography variant="h5" style={{ color: '#f44336' }}>
                  {statistics.inactive}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Operation Types
                </Typography>
                <Typography variant="h5" style={{ color: '#ff9800' }}>
                  {Object.keys(statistics.byOperationType || {}).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Card className={classes.searchFilter}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Search operations..."
                value={filters.search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon color="action" />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.active === undefined ? '' : filters.active.toString()}
                  onChange={(e) => handleFilterChange('active', e.target.value === '' ? undefined : e.target.value === 'true')}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Operation Type</InputLabel>
                <Select
                  value={filters.operationTypeId ?? ''}
                  onChange={(e) => handleFilterChange('operationTypeId', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {operationTypes.map((type) => (
                    <MenuItem key={type.Id} value={type.Id}>
                      {type.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Machine</InputLabel>
                <Select
                  value={filters.machineId ?? ''}
                  onChange={(e) => handleFilterChange('machineId', e.target.value)}
                >
                  <MenuItem value="">All Machines</MenuItem>
                  {machines.map((machine) => (
                    <MenuItem key={machine.Id} value={machine.Id}>
                      {machine.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Part</InputLabel>
                <Select
                  value={filters.partId ?? ''}
                  onChange={(e) => handleFilterChange('partId', e.target.value)}
                >
                  <MenuItem value="">All Parts</MenuItem>
                  {parts.map((part) => (
                    <MenuItem key={part.Id} value={part.Id}>
                      {part.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {bulkSelection.length > 0 && (
        <div className={classes.bulkActions}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">
              {bulkSelection.length} operation(s) selected
            </Typography>
            <Box>
              <Button
                size="small"
                startIcon={<ActiveIcon />}
                onClick={handleBulkActivate}
                style={{ marginRight: 8 }}
              >
                Activate
              </Button>
              <Button
                size="small"
                startIcon={<InactiveIcon />}
                onClick={handleBulkDeactivate}
              >
                Deactivate
              </Button>
            </Box>
          </Box>
        </div>
      )}

      {/* Operations Table */}
      <Card>
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={bulkSelection.length > 0 && bulkSelection.length < operations.length}
                    checked={operations.length > 0 && bulkSelection.length === operations.length}
                    onChange={handleBulkSelectAll}
                  />
                </TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Operation Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operations.map((operation) => (
                <TableRow key={operation.Id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={bulkSelection.includes(operation.Id!)}
                      onChange={() => handleBulkSelectOne(operation.Id!)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.operationNumber}>
                      {operation.OperationNum}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="div">
                      {operation.Name}
                    </Typography>
                  </TableCell>
                  <TableCell>{operation.OperationType?.Name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {operation.Description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={operation.Active ? 'Active' : 'Inactive'}
                      color={operation.Active ? 'primary' : 'default'}
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title="View Inputs">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          style={{ backgroundColor: '#ff9800', color: 'white' }}
                          onClick={() => handleViewInputs(operation)}
                        >
                          <InputIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Work Instructions">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          style={{ backgroundColor: '#4caf50', color: 'white' }}
                          onClick={() => handleViewWorkInstructions(operation)}
                        >
                          <WorkInstructionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Tools & Fixtures">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                          onClick={() => handleViewToolFixtures(operation)}
                        >
                          <ToolFixtureIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          style={{ backgroundColor: '#2196f3', color: 'white' }}
                          onClick={(e) => handleMenuOpen(e, operation.Id!)}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.limit}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditOperation(operations.find(o => o.Id === selectedOperationId))}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={() => selectedOperationId && handleDeleteOperation(selectedOperationId)}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this operation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OperationsList; 